import React, { useState, useEffect } from 'react';
import { Sparkles, Copy, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function TemplatesTab({ onUseTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [userTemplates, setUserTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showUserTemplates, setShowUserTemplates] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchUserTemplates();
  }, []);

  const fetchUserTemplates = async () => {
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get(`${API_URL}/api/user-templates`, {
        withCredentials: true,
        headers
      });
      setUserTemplates(data);
    } catch (error) {
      console.error('Error fetching user templates:', error);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/templates`);
      setTemplates(data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = filter === 'all' 
    ? templates 
    : templates.filter(t => t.function_tag === filter || (!t.function_tag && filter === 'general'));

  const displayTemplates = showUserTemplates ? userTemplates : filteredTemplates;

  const handleUseTemplate = async (template) => {
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${API_URL}/api/prompts`,
        {
          title: template.title,
          content: template.content,
          function_tag: template.function_tag,
          category_tag: template.category_tag,
          model_tag: template.model_tag,
          commentary: template.commentary,
          is_private: true
        },
        { withCredentials: true, headers }
      );
      toast.success('Template added to your library');
      if (onUseTemplate) onUseTemplate();
    } catch (error) {
      toast.error('Failed to add template');
    }
  };

  const handleCopy = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const handleDeleteUserTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;

    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${API_URL}/api/user-templates/${templateId}`, {
        withCredentials: true,
        headers
      });
      toast.success('Template deleted');
      fetchUserTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C4C3B] mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="templates-tab">
      {/* Toggle between system and user templates */}
      <div className="flex space-x-1 rounded-lg bg-[#F5F2EA] p-1">
        <button
          onClick={() => setShowUserTemplates(false)}
          className={`flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md flex-1 transition-colors ${
            !showUserTemplates
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="system-templates-btn"
        >
          System ({templates.length})
        </button>
        <button
          onClick={() => setShowUserTemplates(true)}
          className={`flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md flex-1 transition-colors ${
            showUserTemplates
              ? 'bg-white text-[#1C1917] shadow-sm'
              : 'text-[#78716C] hover:text-[#1C1917]'
          }`}
          data-testid="user-templates-btn"
        >
          My Templates ({userTemplates.length})
        </button>
      </div>

      {/* Filter Tabs - only for system templates */}
      {!showUserTemplates && (
        <div className="flex gap-1 overflow-x-auto pb-2">
          {['all', 'general', 'Sales', 'Marketing', 'Customer Success', 'Engineering', 'Product'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs font-medium rounded-md whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-[#2C4C3B] text-white'
                  : 'bg-[#F5F2EA] text-[#78716C] hover:bg-[#E7E5E4]'
              }`}
              data-testid={`filter-${f.toLowerCase().replace(' ', '-')}`}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      )}

      {/* Templates List */}
      <div className="space-y-3">
        {displayTemplates.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles size={48} className="mx-auto text-[#E7E5E4] mb-3" />
            <p className="text-sm text-[#78716C]">
              {showUserTemplates ? 'No custom templates yet. Save prompts as templates!' : 'No templates in this category'}
            </p>
          </div>
        ) : (
          displayTemplates.map((template, idx) => (
            <div
              key={showUserTemplates ? template.template_id : idx}
              className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm overflow-hidden flex flex-col p-4"
              data-testid={`template-${idx}`}
            >
              <h3 className="text-base tracking-tight font-medium text-[#1C1917] mb-2">{template.title}</h3>
              
              {/* Tags */}
              <div className="flex flex-wrap gap-1 mb-3">
                {template.function_tag && (
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#EAF0EC] text-[#2C4C3B]">
                    {template.function_tag}
                  </span>
                )}
                {template.category_tag && (
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#F5F2EA] text-[#78716C]">
                    {template.category_tag}
                  </span>
                )}
                {template.model_tag && (
                  <span className="inline-flex items-center rounded-md px-2 py-1 text-[10px] font-medium tracking-wide uppercase bg-[#EAF0EC] text-[#2C4C3B]">
                    {template.model_tag}
                  </span>
                )}
              </div>

              <p className="text-sm leading-relaxed text-[#57534E] mb-2 line-clamp-2">{template.content}</p>
              
              {template.commentary && (
                <p className="text-xs text-[#78716C] italic mb-3">{template.commentary}</p>
              )}

              <div className="flex gap-2">
                {!showUserTemplates ? (
                  <>
                    <button
                      onClick={() => handleUseTemplate(template)}
                      className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-[#2C4C3B] text-white hover:bg-[#243D30] flex-1"
                      data-testid="use-template-button"
                    >
                      <Plus size={12} className="mr-1" /> Use Template
                    </button>
                    <button
                      onClick={() => handleCopy(template.content)}
                      className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9]"
                      data-testid="copy-template-button"
                    >
                      <Copy size={12} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleUseTemplate(template)}
                      className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors bg-[#2C4C3B] text-white hover:bg-[#243D30] flex-1"
                      data-testid="use-template-button"
                    >
                      <Plus size={12} className="mr-1" /> Use
                    </button>
                    <button
                      onClick={() => handleCopy(template.content)}
                      className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9]"
                      data-testid="copy-template-button"
                    >
                      <Copy size={12} />
                    </button>
                    <button
                      onClick={() => handleDeleteUserTemplate(template.template_id)}
                      className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-xs font-medium transition-colors border border-red-200 bg-white text-red-600 hover:bg-red-50"
                      data-testid="delete-template-button"
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}